import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';

interface ClinicDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  actions?: React.ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
  closeButtonSide?: 'left' | 'right';
  zIndex?: number;
  fullHeight?: boolean;
}

export const ClinicDialog: React.FC<ClinicDialogProps> = ({
  open,
  onClose,
  title,
  maxWidth = 'sm',
  loading = false,
  actions,
  onSubmit,
  children,
  closeButtonSide = 'right',
  zIndex,
  fullHeight = false,
}) => {
  const inner = (
    <>
      <DialogTitle sx={CLINIC_DIALOG_SX.title}>
        {title}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            ...(closeButtonSide === 'left' ? { left: 8 } : { right: 8 }),
            top: 8,
            color: POPUP_COLORS.titleText,
            '&:hover': { color: POPUP_COLORS.closeBtnHover },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ ...CLINIC_DIALOG_SX.content, ...(fullHeight && { display: 'flex', flexDirection: 'column', overflow: 'hidden' }) }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
            <CircularProgress sx={CLINIC_DIALOG_SX.spinner} />
          </Box>
        ) : (
          children
        )}
      </DialogContent>
      {actions && <DialogActions sx={CLINIC_DIALOG_SX.actions}>{actions}</DialogActions>}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{ sx: { ...CLINIC_DIALOG_SX.paper, ...(fullHeight && { height: '80vh' }) } }}
      sx={zIndex !== undefined ? { zIndex } : undefined}
    >
      {onSubmit ? <form onSubmit={onSubmit}>{inner}</form> : inner}
    </Dialog>
  );
};
